import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  FileSpreadsheet, 
  Link2, 
  CheckCircle, 
  XCircle, 
  Loader2,
  ArrowRight,
  AlertTriangle,
  Table,
  Download
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { ticketService } from '../../services/api';

// Available database fields for mapping
const DB_FIELDS = {
  ACHAT: [
    { value: 'evenement', label: 'Match/Événement' },
    { value: 'categorie', label: 'Catégorie' },
    { value: 'date_evenement', label: 'Date événement' },
    { value: 'qty', label: 'Quantité' },
    { value: 'prix', label: 'Prix' },
    { value: 'email_compte', label: 'Email du compte' },
    { value: 'pw_compte', label: 'Mot de passe compte' },
    { value: 'ref_achat', label: 'Référence achat' },
    { value: 'source', label: 'Source/Plateforme' },
    { value: 'lieu', label: 'Lieu/Stade' },
    { value: 'ville', label: 'Ville' },
    { value: 'pays', label: 'Pays' },
    { value: 'note', label: 'Note' },
  ],
  VENTE: [
    { value: 'evenement', label: 'Match/Événement' },
    { value: 'categorie', label: 'Catégorie' },
    { value: 'date_evenement', label: 'Date événement' },
    { value: 'date_operation', label: 'Date vente' },
    { value: 'qty', label: 'Quantité' },
    { value: 'prix', label: 'Prix' },
    { value: 'ref_vente', label: 'Référence vente' },
    { value: 'source', label: 'Plateforme' },
    { value: 'lieu', label: 'Lieu/Stade' },
    { value: 'ville', label: 'Ville' },
    { value: 'pays', label: 'Pays' },
    { value: 'nom_client', label: 'Nom client' },
    { value: 'mail_client', label: 'Email client' },
    { value: 'note', label: 'Note' },
  ]
};

export const GoogleSheetsImportModal = ({ 
  open, 
  onOpenChange, 
  onImportComplete,
  project = 'WORLD CUP 2026'
}) => {
  const [step, setStep] = useState(1); // 1: URL, 2: Type, 3: Mapping, 4: Import
  const [url, setUrl] = useState('');
  const [ticketType, setTicketType] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setUrl('');
      setTicketType('');
      setTestResult(null);
      setPreviewData(null);
      setColumnMapping({});
      setImportResult(null);
    }
  }, [open]);

  // Test Google Sheet access
  const testAccess = async () => {
    if (!url.trim()) {
      toast.error('Entrez une URL Google Sheets');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const result = await ticketService.testGoogleSheetAccess(url);
      setTestResult(result);
      
      if (result.accessible) {
        toast.success(`Sheet accessible! ${result.row_count} lignes trouvées`);
      } else {
        toast.error(result.error || 'Sheet non accessible');
      }
    } catch (error) {
      setTestResult({ accessible: false, error: error.message });
      toast.error('Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  // Load preview data
  const loadPreview = async () => {
    if (!ticketType) {
      toast.error('Sélectionnez le type de ticket');
      return;
    }

    setLoading(true);
    try {
      const result = await ticketService.previewGoogleSheet(url, ticketType, project);
      setPreviewData(result);
      
      // Auto-map columns based on header names
      const autoMapping = {};
      const fields = DB_FIELDS[ticketType] || [];
      
      result.headers.forEach(header => {
        const headerLower = header.toLowerCase().trim();
        
        // Try to auto-match
        for (const field of fields) {
          const fieldLower = field.label.toLowerCase();
          if (headerLower.includes(fieldLower) || 
              fieldLower.includes(headerLower) ||
              headerLower === field.value) {
            autoMapping[header] = field.value;
            break;
          }
        }
        
        // Specific mappings
        if (headerLower.includes('match') || headerLower.includes('event')) {
          autoMapping[header] = 'evenement';
        } else if (headerLower.includes('cat')) {
          autoMapping[header] = 'categorie';
        } else if (headerLower.includes('ref') && ticketType === 'VENTE') {
          autoMapping[header] = 'ref_vente';
        } else if (headerLower.includes('ref') && ticketType === 'ACHAT') {
          autoMapping[header] = 'ref_achat';
        } else if (headerLower.includes('qty') || headerLower.includes('quant')) {
          autoMapping[header] = 'qty';
        } else if (headerLower.includes('prix') || headerLower.includes('price')) {
          autoMapping[header] = 'prix';
        }
      });
      
      setColumnMapping(autoMapping);
      setStep(3);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Update column mapping
  const updateMapping = (header, dbField) => {
    setColumnMapping(prev => ({
      ...prev,
      [header]: dbField || undefined
    }));
  };

  // Perform import
  const performImport = async () => {
    // Filter out unmapped columns
    const validMapping = Object.fromEntries(
      Object.entries(columnMapping).filter(([_, v]) => v)
    );

    if (Object.keys(validMapping).length === 0) {
      toast.error('Mappez au moins une colonne');
      return;
    }

    setImporting(true);
    try {
      const result = await ticketService.importGoogleSheet(
        url, 
        ticketType, 
        validMapping, 
        project
      );
      
      setImportResult(result);
      setStep(4);
      
      if (result.created > 0) {
        toast.success(`${result.created} tickets importés!`);
        onImportComplete?.();
      } else {
        toast.warning('Aucun nouveau ticket importé');
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="h-5 w-5 text-green-500" />
            Import Google Sheets
          </DialogTitle>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 py-2 border-b border-zinc-700">
          {[
            { num: 1, label: 'URL' },
            { num: 2, label: 'Type' },
            { num: 3, label: 'Mapping' },
            { num: 4, label: 'Résultat' }
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              {i > 0 && <div className="w-8 h-0.5 bg-zinc-600" />}
              <div className={cn(
                "flex items-center gap-1.5 px-3 py-1 rounded-full text-sm",
                step >= s.num 
                  ? "bg-[#C4A052]/20 text-[#C4A052]" 
                  : "bg-zinc-800 text-zinc-500"
              )}>
                <span className="font-semibold">{s.num}</span>
                <span>{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="py-4 space-y-4">
          {/* Step 1: URL Input */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-300">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                  <div>
                    <strong>Pré-requis :</strong>
                    <p className="mt-1">Le Google Sheet doit être partagé avec l'option "Toute personne disposant du lien peut consulter"</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>URL du Google Sheet</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                    <Input
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      className="pl-10"
                    />
                  </div>
                  <Button 
                    onClick={testAccess}
                    disabled={testing || !url.trim()}
                    variant="outline"
                    className="border-[#C4A052]/50 text-[#C4A052]"
                  >
                    {testing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Tester'
                    )}
                  </Button>
                </div>
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={cn(
                  "p-4 rounded-lg border",
                  testResult.accessible 
                    ? "bg-emerald-500/10 border-emerald-500/30" 
                    : "bg-red-500/10 border-red-500/30"
                )}>
                  <div className="flex items-center gap-2">
                    {testResult.accessible ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-emerald-400" />
                        <span className="text-emerald-300">
                          Sheet accessible! {testResult.row_count} lignes de données
                        </span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5 text-red-400" />
                        <span className="text-red-300">{testResult.error}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  onClick={() => setStep(2)}
                  disabled={!testResult?.accessible}
                  className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                >
                  Continuer
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Ticket Type */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Type de tickets à importer</Label>
                <Select value={ticketType} onValueChange={setTicketType}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sélectionnez le type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACHAT">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500" />
                        ACHAT - Tickets achetés
                      </span>
                    </SelectItem>
                    <SelectItem value="VENTE">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        VENTE - Tickets vendus
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>
                  Retour
                </Button>
                <Button
                  onClick={loadPreview}
                  disabled={!ticketType || loading}
                  className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Table className="h-4 w-4 mr-2" />
                  )}
                  Charger les données
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Column Mapping */}
          {step === 3 && previewData && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-400">
                {previewData.total_rows} lignes trouvées. Mappez les colonnes du Sheet vers les champs de la base de données.
              </div>

              {/* Mapping Table */}
              <div className="border border-zinc-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-zinc-400 font-medium">Colonne Sheet</th>
                      <th className="px-4 py-2 text-center text-zinc-400 font-medium">→</th>
                      <th className="px-4 py-2 text-left text-zinc-400 font-medium">Champ Base de Données</th>
                      <th className="px-4 py-2 text-left text-zinc-400 font-medium">Exemple</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-700">
                    {previewData.headers.map((header, idx) => (
                      <tr key={header} className="hover:bg-zinc-800/50">
                        <td className="px-4 py-2 font-medium text-white">{header}</td>
                        <td className="px-4 py-2 text-center text-zinc-500">
                          <ArrowRight className="h-4 w-4 inline" />
                        </td>
                        <td className="px-4 py-2">
                          <Select 
                            value={columnMapping[header] || ''} 
                            onValueChange={(v) => updateMapping(header, v)}
                          >
                            <SelectTrigger className="w-full h-8 text-xs">
                              <SelectValue placeholder="-- Ignorer --" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">-- Ignorer --</SelectItem>
                              {(DB_FIELDS[ticketType] || []).map(field => (
                                <SelectItem key={field.value} value={field.value}>
                                  {field.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-2 text-xs text-zinc-500 truncate max-w-[150px]">
                          {previewData.preview[0]?.[idx] || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Preview Data */}
              <details className="text-sm">
                <summary className="cursor-pointer text-zinc-400 hover:text-white">
                  Voir l'aperçu des données ({Math.min(5, previewData.preview.length)} premières lignes)
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs border border-zinc-700 rounded">
                    <thead className="bg-zinc-800">
                      <tr>
                        {previewData.headers.map(h => (
                          <th key={h} className="px-2 py-1 text-left text-zinc-400">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.preview.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t border-zinc-700">
                          {row.map((cell, j) => (
                            <td key={j} className="px-2 py-1 text-zinc-300 truncate max-w-[100px]">
                              {cell || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Retour
                </Button>
                <Button
                  onClick={performImport}
                  disabled={importing || Object.values(columnMapping).filter(v => v).length === 0}
                  className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                >
                  {importing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Importer {previewData.total_rows} lignes
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 4: Import Result */}
          {step === 4 && importResult && (
            <div className="space-y-4">
              <div className={cn(
                "p-6 rounded-lg border text-center",
                importResult.created > 0 
                  ? "bg-emerald-500/10 border-emerald-500/30" 
                  : "bg-amber-500/10 border-amber-500/30"
              )}>
                {importResult.created > 0 ? (
                  <>
                    <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-emerald-300">
                      Import réussi!
                    </h3>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-amber-300">
                      Aucun nouveau ticket
                    </h3>
                  </>
                )}
                
                <div className="mt-4 space-y-1 text-sm">
                  <p className="text-emerald-300">
                    <strong>{importResult.created}</strong> tickets créés
                  </p>
                  <p className="text-amber-300">
                    <strong>{importResult.skipped}</strong> doublons ignorés
                  </p>
                </div>

                {importResult.errors?.length > 0 && (
                  <div className="mt-4 text-left bg-red-500/10 border border-red-500/30 rounded p-3">
                    <p className="text-red-400 font-medium mb-1">Erreurs :</p>
                    <ul className="text-xs text-red-300 space-y-1">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>• {err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  onClick={() => onOpenChange(false)}
                  className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                >
                  Fermer
                </Button>
              </DialogFooter>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleSheetsImportModal;
